import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, MapPin, CheckCircle, Clock, ArrowRight } from "@phosphor-icons/react";

export default function ScanPass() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [code, setCode] = useState("");
  const [proximity, setProximity] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    if (!user) return;
    api.get("/bookings/mine").then((r) => setBookings(r.data || []));
  };
  useEffect(load, [user]);

  if (!user) return <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="scan-signin">Sign in as a Professional to scan.</div>;
  if (user.role !== "professional" && user.role !== "owner") {
    return <div className="max-w-md mx-auto px-6 py-16 text-center text-cream/70" data-testid="scan-not-pro">Only Professionals can scan a KUTZ Pass.</div>;
  }

  const submit = async (bid, kind) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/bookings/${bid}/${kind}`, { pass_code: code.trim().toUpperCase() });
      setResult({ kind, data });
      toast.success(kind === "checkin" ? "Ben checked in" : "Appointment complete");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed");
    } finally { setBusy(false); }
  };

  // Auto-fill from a matching booking on paste of code
  const matched = bookings.find(b => b.pass_code === code.trim().toUpperCase());

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-24" data-testid="scan-pass-page">
      <p className="overline mb-2">Scan KUTZ Pass</p>
      <h1 className="font-display text-4xl tracking-tighter">Verify a booking.</h1>

      <div className="mt-8 border border-cream/10 p-6 bg-cream/[0.02]">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-cream/60">
          <Camera size={16} color="#C5A059" /> Camera · DEMO / SIMULATED
        </div>
        <p className="text-xs text-cream/50 mt-2">Type or paste the Member's fallback code — real QR scanner ships post-prototype.</p>
        <input
          data-testid="scan-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. A3F9B2E1C7D4"
          className="input-line mt-4 tracking-[0.3em]"
        />
        <label className="mt-4 flex items-center gap-3 cursor-pointer" data-testid="proximity-toggle">
          <input type="checkbox" checked={proximity} onChange={(e) => setProximity(e.target.checked)} className="accent-[#C5A059] w-5 h-5" />
          <span className="text-sm text-cream/80 flex items-center gap-1">
            <MapPin size={14} color="#C5A059" weight="fill" /> Location proximity · DEMO / SIMULATED
          </span>
        </label>
      </div>

      {matched && (
        <div className="mt-6 border border-gold/40 p-5" data-testid={`match-${matched.id}`}>
          <p className="overline text-gold">Match found</p>
          <p className="font-display text-xl mt-1">{matched.member_name} · {matched.service_name}</p>
          <p className="text-xs text-cream/60 mt-1">{new Date(matched.start_time).toLocaleString()} · status: {matched.status}</p>
          <div className="mt-5 flex gap-3">
            {!matched.check_in && (
              <button
                data-testid="scan-checkin"
                disabled={busy || !proximity}
                onClick={() => submit(matched.id, "checkin")}
                className="btn-gold px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
              >
                Check In <ArrowRight size={12} weight="bold" />
              </button>
            )}
            {matched.check_in && !matched.check_out && (
              <button
                data-testid="scan-checkout"
                disabled={busy || !proximity}
                onClick={() => submit(matched.id, "checkout")}
                className="btn-gold px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
              >
                Check Out <ArrowRight size={12} weight="bold" />
              </button>
            )}
            {matched.status === "completed" && <p className="text-xs text-gold">Completed at {matched.check_out ? new Date(matched.check_out).toLocaleTimeString() : "—"}</p>}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-6 border border-gold/40 bg-gold/5 p-5 flex items-start gap-3" data-testid="scan-result">
          <CheckCircle size={22} weight="fill" color="#C5A059" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{result.kind === "checkin" ? "Ben checked in" : "Appointment complete"}</p>
            <p className="text-xs text-cream/70 mt-1 flex items-center gap-1"><Clock size={12} /> {new Date().toLocaleTimeString()}</p>
            {result.kind === "checkout" && result.data?.dispute_window_end && (
              <p className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mt-2">Six-hour service-dispute window opens until {new Date(result.data.dispute_window_end).toLocaleTimeString()}.</p>
            )}
          </div>
        </div>
      )}

      <div className="mt-10">
        <p className="overline mb-3">Today's bookings</p>
        <div className="space-y-2">
          {bookings.filter(b => b.status !== "cancelled").slice(0, 6).map((b) => (
            <button
              key={b.id}
              onClick={() => setCode(b.pass_code)}
              data-testid={`scan-quick-${b.id}`}
              className="w-full text-left p-3 border border-cream/10 hover:border-gold/40 flex items-center justify-between"
            >
              <div>
                <p className="text-sm text-cream">{b.member_name} · {b.service_name}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">{b.status} · {new Date(b.start_time).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}</p>
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Load code</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
